import { skipToken } from "@reduxjs/toolkit/query";

import { newrelic } from "@jti/fe-clients";
import {
  useApplyCouponsToCartMutation,
  useGetCustomerCouponsQuery,
  useRemoveCouponsFromCartMutation,
} from "@jti/store/slices/magentoApiSlice";
import { useTranslations } from "@jti/ui";

import { getErrorMessage } from "@checkout-v2/shared/utils";

type UseCouponsArgs = {
  cartId: string | undefined;
  skip?: boolean;
};

export const useCoupons = ({ cartId, skip }: UseCouponsArgs) => {
  const { __ } = useTranslations();

  const { data: couponsData } = useGetCustomerCouponsQuery(cartId || skipToken, {
    skip,
  });

  const coupons = couponsData?.coupons ?? [];

  const couponCodes = coupons
    .map((coupon) => coupon.code)
    .filter((code): code is string => Boolean(code?.trim().length));

  const [
    applyCoupons,
    { isError: isApplyCouponsError, error: applyCouponsError, reset: resetApplyCoupons, isLoading: isApplyingCoupons },
  ] = useApplyCouponsToCartMutation();
  const [removeCoupons, { isLoading: isRemovingCoupons }] = useRemoveCouponsFromCartMutation();

  const errors = isApplyCouponsError
    ? [getErrorMessage(applyCouponsError) || __("cart.checkout.error.promo_code")]
    : undefined;

  const handleAddCoupon = async (newCode: string) => {
    if (!cartId) {
      newrelic.noticeError(new Error(`useCoupons: adding coupon without cartId`));
      return;
    }
    const next = newCode.trim();
    if (!next) {
      newrelic.noticeError(new Error(`useCoupons: adding empty coupon`));
      return;
    }

    const newCodes = couponCodes.includes(next) ? couponCodes : [...couponCodes, next];
    await applyCoupons({ cartId, couponCodes: newCodes });
  };

  const handleRemoveCoupon = async (couponToRemove: string) => {
    if (!cartId) {
      newrelic.noticeError(new Error(`useCoupons: removing coupon without cartId`));
      return;
    }
    await removeCoupons({ cartId, couponCodes: [couponToRemove] });
  };

  const handleRemoveAllCoupons = async () => {
    if (!cartId) {
      newrelic.noticeError(new Error(`useCoupons: removing coupons without cartId`));
      return;
    }
    if (couponCodes.length === 0) {
      newrelic.noticeError(new Error(`useCoupons: removing empty coupons`));
      return;
    }
    await removeCoupons({ cartId, couponCodes });
  };

  return {
    coupons,
    isLoading: isApplyingCoupons || isRemovingCoupons,
    errors,
    canApplyCoupons: !!couponsData?.canApplyCoupons,
    onAddCoupon: handleAddCoupon,
    onRemoveCoupon: handleRemoveCoupon,
    onRemoveAllCoupons: handleRemoveAllCoupons,
    resetApplyCoupons,
  };
};
